import { API_DOMAIN, SYNC_REPORT_URL } from 'config/constants';
import { api } from 'lib/api';
import { checkStatus, getSearch } from 'lib/helpers';
import { z } from 'zod';
import { zAccess } from './access';

export const zUser = z
	.object({
		username: z.string().default('defaultUsername'),
		email: z.string().default('defaultEmail'),
		first_name: z.string().default('defaultFirstName'),
		last_name: z.string(),
	})
	.transform((data) => ({
		...data,
		type: 'default' as UserType,
		status: 'active' as UserStatusType,
		Rules: {} as ServerRules,
	}));

export const zOfflineItems = z.object({
	access: z.string().or(zAccess),
	status: z.enum(['success', 'error']),
	movements: z.object({
		type: z.string(),
		date: z.date(),
		plate: z.string().optional(),
		userId: zUser,
		generatedBy: z.string().optional(),
		_id: z.string().optional(),
	}),
	msgError: z.string(),
});

export const zAccessOffline = z.object({
	processSyncId: z.number(),
	userProcessSync: zUser,
	offlineItems: z.array(zOfflineItems),
	createdAt: z.date(),
});

export type FetchSyncReportsSearchProps = {
	search?: string | null;
	from?: string;
};

export type FetchSyncReports = FetchSyncReportsSearchProps & PaginationProps;

export async function fetchSyncReports(
	props: FetchSyncReports
): Promise<{ total: number; elements: PartialAccessOffline[] }> {
	try {
		const sort: Sort = {
			[props.orderBy || '']: props.order || 'asc',
		};

		const { filter, options } = getSearch(sort, props);

		const response = await api.get<{ total: number; elements: PartialAccessOffline[] }>(
			`${API_DOMAIN}${SYNC_REPORT_URL}`,
			{
				params: { filter, options },
			}
		);
		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}
		if (!Array.isArray(response.data.elements) || response.data.elements.length < 1) {
			throw new Error('invalid response');
		}
		return response.data;
	} catch (error) {
		console.error(error);
		return { total: 0, elements: [] };
	}
}

export async function fetchSyncReport(id: string): Promise<AccessOffline | null> {
	try {
		const response = await api.get<AccessOffline>(`${API_DOMAIN}${SYNC_REPORT_URL}/${id}`);
		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		return response.data;
	} catch (error) {
		console.error(error);
		return null;
	}
}

export async function fetchExportSyncReport(id: string) {
	try {
		const response = await api.get(`${API_DOMAIN}${SYNC_REPORT_URL}/${id}/export`, {
			responseType: 'blob', // Indica que la respuesta es un archivo
		});

		// Crear un enlace para descargar el archivo
		const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', `accessOffline_${id}.csv`);
		document.body.appendChild(link);
		link.click();

		// Limpiar el objeto URL
		window.URL.revokeObjectURL(url);
	} catch (error) {
		console.error('Error exporting CSV:', error);
	}
}

/*
export async function fetchSyncReport(id: string): Promise<AccessOffline | null> {
	try {
		const response = await api.get(`${API_DOMAIN}${ACCESS_URL}/${id}`);

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		const data = zAccessOffline.safeParse(response.data);

		if (!data.success) {
			console.error(data.error);
			return null;
		}

		return data.data;
	} catch (error) {
		console.error(error);
		return null;
	}
}
*/
